import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { NgClass, NgStyle } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule, UntypedFormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo, faSave } from '@fortawesome/free-solid-svg-icons';
import { AngularEditorModule } from '@kolkov/angular-editor';
import { FileUploadModule } from 'ng2-file-upload';
import { BehaviorSubject, distinctUntilChanged } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { Controls } from 'src/app/controls/forms/managed-form/ngx-sub-form-utils';
import { ManagementFormComponent } from 'src/app/controls/forms/management-form/management-form';
import { ContactDataType } from 'src/app/dialogs/contacts/models/contact-data-type';
import { RichToolTipDirective } from 'src/app/directives/rich-tool-tip.directive';
import { Attribute } from 'src/app/models/attribute';
import { PropertyType } from 'src/app/models/enums/property-type';
import { ISmsTemplate } from 'src/app/models/interfaces/sms-template.interface';
import { Sms } from 'src/app/models/sms';
import { SmsTemplate } from 'src/app/models/sms-template';
import { AttributeDefinitionsService } from 'src/app/services/attribute-definitions.service';
import { DialogService } from 'src/app/services/dialog.service';
import { MemoryService } from 'src/app/services/memory.service';
import { SmsTemplatesService } from 'src/app/services/sms-templates.service';
import { ThemesService } from 'src/app/services/themes.service';
import { FormFocusDirective } from '../../../controls/directives/form-focus.directive';
import { TemplateContextMenuItem } from '../../emails-templates/email-template-form/Interfaces/template-context-menu-item';
import { TemplateContextMenuItemEvent } from '../../emails-templates/email-template-form/Interfaces/template-context-menu-item-event';
import { TemplateContextMenuComponent } from '../../emails-templates/email-template-form/template-context-menu/template-context-menu.component';
import { SmsTemplateFormContext } from '../models/sms-template-form-context';

@Component({
    selector: 'app-sms-template-form',
    imports: [
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        MatIconModule,
        MatTooltipModule,
        FileUploadModule,
        MatFormFieldModule,
        MatInputModule,
        FaIconComponent,
        MatSelectModule,
        MatOptionModule,
        MatChipsModule,
        AngularEditorModule,
        TemplateContextMenuComponent,
        NgStyle,
        MatButtonModule,
        NgClass,
        RichToolTipDirective,
    ],
    templateUrl: './sms-template-form.component.html',
    styleUrl: './sms-template-form.component.scss',
})
export class SmsTemplateFormComponent extends ManagementFormComponent<SmsTemplate, ISmsTemplate> implements OnInit, OnChanges {
    /**
     * Composant du menu contextuel
     */
    @ViewChild(TemplateContextMenuComponent)
    contextMenu: TemplateContextMenuComponent;

    /**
     * Liste des icônes utilisés dans le template
     */
    public faSave = faSave;
    public faCircleInfo = faCircleInfo;

    /**
     * Liste des enums utilisés dans le template
     */
    public FormMode = FormMode;

    /**
     * Données du modèle du SMS.
     */
    public sms: Sms = new Sms();

    /**
     * Controles du formulaire.
     */
    public contentFormControl: UntypedFormControl = new UntypedFormControl(null);
    public recipientsFormControl: UntypedFormControl = new UntypedFormControl(null, {
        updateOn: 'change',
    });
    public recipientInputFormControl: UntypedFormControl = new UntypedFormControl(null);

    /**
     * Touches utilisées pour valider la saisie d'un n° de téléphone.
     */
    readonly separatorKeysCodes: number[] = [ENTER, 190, COMMA];

    /**
     * Valeur indiquant si le menu contextuel est visible.
     */
    isDisplayContextMenu: boolean;

    /**
     * Liste des éléments du menu contextuel.
     */
    contextMenuItems: Array<TemplateContextMenuItem> = [];

    /**
     * Position sur l'axe X de la sourris au moement d'un clic droit (utilisé pour positionner le menu contextuel)
     */
    rightClickMenuPositionX: number;

    /**
     * Position sur l'axe Y de la sourris au moement d'un clic droit (utilisé pour positionner le menu contextuel)
     */
    rightClickMenuPositionY: number;

    /**
     * Index du début du texte sélectionné dans le champ sujet (utilisé pour le placement d'une variable avec le menu contextuel).
     */
    currentSelectionStart: number;

    /**
     * Index de fin du texte sélectionné dans le champ sujet (utilisé pour le placement d'une variable avec le menu contextuel).
     */
    currentSelectionEnd: number;

    /**
     * Texte sélectionné dans le champ body (utilisé pour le placement d'une variable avec le menu contextuel).
     */
    currentRange: Range;

    /**
     * Liste des attributs personnalisés de l'organisation (utilisé par le menu contextuel d'ajout de variables).
     */
    attributes: Attribute[] = [];

    /**
     * Infos du SMS à afficher dans le formulaire.
     */
    // @DataInput()
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('smsTemplate')
    public dataInput: Required<SmsTemplate> | null | undefined;

    /**
     * Contexte du formulaire (mode d'affichage ...).
     */
    public _context: SmsTemplateFormContext;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<SmsTemplateFormContext> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: SmsTemplateFormContext) {
        this._context$.next(value);
    }

    /*
     * Indique si le composant est en mode debug (affiche des informations pour le developpeur)
     */
    public debugMode = false;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output('smsTemplateUpdated')
    public dataOutput: EventEmitter<SmsTemplate> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annulé(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    /**
     * Taille maximale d'un SMS (160 caractères).
     */
    private smsMaxLength = 160;

    /**
     * Nombre maximal de SMS (10).
     */
    private smsMaxParts = 10;

    @ViewChild('progressBar', { read: ElementRef, static: true })
    progressBar: ElementRef;

    @ViewChild('charsCounter', { read: ElementRef, static: true })
    charsCounter: ElementRef;

    /**
     * Constructeur
     */
    constructor(
        public dialog: MatDialog,
        private memoryService: MemoryService,
        private smsTemplatesService: SmsTemplatesService,
        private attributesService: AttributeDefinitionsService,
        private dialogService: DialogService,
        private themesService: ThemesService
    ) {
        super();
    }

    /**
     * Initialise le composant.
     */
    override ngOnInit() {
        super.ngOnInit();

        this.contentFormControl.valueChanges.pipe(distinctUntilChanged()).subscribe((value) => {
            if (this._context.mode != FormMode.Reading) {
                this.displayProgressBar(value);
            }
            this.sms.content = this.contentFormControl.value;
            this.formGroup.get('template').setValue(this.sms);
        });

        // S'abonne aux changements de contexte
        this._context$.subscribe((x) => {
            this._context = x;
            this.dataInput$.next(x.item);

            if (!!x.item.template) {
                this.sms = x.item.template;
                this.recipientsFormControl.clearValidators();
                this.recipientsFormControl.addValidators(MaxRecipientsValidator(this.sms));
                this.recipientInputFormControl.clearValidators();
                this.recipientInputFormControl.addValidators(Validators.pattern(this.smsPattern));

                this.contentFormControl.setValue(this.sms.content);
            } else {
                this.sms = {
                    from: '',
                    to: [],
                    content: '',
                };

                this.recipientsFormControl.clearValidators();
                this.recipientsFormControl.addValidators(MaxRecipientsValidator(this.sms));
                this.recipientInputFormControl.clearValidators();
                this.recipientInputFormControl.addValidators(Validators.pattern(this.smsPattern));

                this.contentFormControl.setValue('');
            }

            if (this._context.mode == FormMode.Reading) {
                this.contentFormControl.disable();
                this.recipientsFormControl.disable();
                this.recipientInputFormControl.disable();
            } else {
                this.displayProgressBar(this.sms.content);
            }
        });

        if (this._context.global !== true) {
            this.attributesService.List(null, null, null, null).subscribe((x) => {
                this.attributes = x.value;
            });
        } else {
            this.attributesService.SystemList(null, null, null, null).subscribe((x) => {
                this.attributes = x.value;
            });
        }
    }

    /**
     * Utilisé pour rendre le champ id read-only
     */
    ngOnChanges() {
        this.formGroup.get('id').disable();

        if (this.contentFormControl && this.recipientsFormControl) {
            if (this._context?.mode == FormMode.Reading) {
                this.contentFormControl.disable();
                this.recipientsFormControl.disable();
                this.recipientInputFormControl.disable();
            } else {
                this.contentFormControl.enable();
                this.recipientsFormControl.enable();
                this.recipientInputFormControl.enable();
            }
        }
    }

    /**
     * Retourne la liste des contrôles du formulaire
     */
    protected getFormControls(): Controls<ISmsTemplate> {
        return {
            id: new UntypedFormControl(null, Validators.required),
            //organizationId: new UntypedFormControl(null),
            name: new UntypedFormControl(null, Validators.required),
            template: new UntypedFormControl(this.sms),
        };
    }

    /**
     *  Appellé lorsque l'utilisateur clique sur le bouton 'sauver'
     */
    public override manualSave(): void {
        this.sms.content = this.contentFormControl.value;

        this.formGroup.get('template').setValue(this.sms);
        super.manualSave();
    }

    /**
     * Appelée lorsque l'utilisateur valide la saisie de n° de téléphone avec la touche ENTREE, virgule, point-virgule ou en quittant le champ de saisie.
     * @param event : Événement émis par le composant de saisie de n° de téléphone.
     */
    public addMobileNumber(event: MatChipInputEvent): void {
        const input = event.chipInput.inputElement;
        const value = event.value.trim();

        if (value !== '') {
            if (!!value && new RegExp(this.smsPattern).test(value)) {
                input.value = '';

                this.sms.to.push(value);
                this.recipientsFormControl.markAsPristine();
                this.recipientsFormControl.markAsUntouched();
                this.recipientInputFormControl.markAsPristine();
                this.recipientInputFormControl.markAsUntouched();
            } else {
                this.recipientInputFormControl.setValue('');
            }

            this.recipientsFormControl.updateValueAndValidity();
        }

        this.formGroup.get('template').setValue(this.sms);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur l'icône de suppression d'un n° de téléphone.
     * @param smsNumber : n° de téléphone à supprimer
     */
    public removeMobileNumber(smsNumber: string): void {
        this.sms.to.splice(this.sms.to.indexOf(smsNumber), 1);
        this.formGroup.get('template').setValue(this.sms);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur l'icône des contacts.
     */
    public displayContacts(): void {
        const dialogRef = this.dialogService.openContacts(ContactDataType.Sms).subscribe((x) => {
            if (!!x) {
                x.toAddresses.forEach((mobileNumber) => {
                    this.sms.to.push(mobileNumber);
                });
            }
        });
    }

    /**
     * Levé lorsque l'utilisateur fait un clic-droit sur un composant listant des n° de téléphone.
     * @param source : Identifiant du composant. Permet de déterminer dans quel composant le paramètre sera ajouté.
     * @param event : Événement souris.
     */
    public onChipListRightClick(source: string, event: MouseEvent) {
        this.isDisplayContextMenu = true;

        this.contextMenuItems = this.getContextMenuItems(source);

        // Position de la souris utilisé pour placer le menu contextuel.
        this.rightClickMenuPositionX = event.clientX;
        this.rightClickMenuPositionY = event.clientY;

        if (this.contextMenuItems.length > 0) {
            this.contextMenu.openMenu(event);
        }
    }

    /**
     * Levé lorsque l'utilisateur fait un clic-droit sur un composant Input.
     * @param source : Identifiant du composant. Permet de déterminer dans quel composant le paramètre sera ajouté.
     * @param event : Événement souris.
     */
    public onInputRightClick(source: string, event: MouseEvent) {
        this.isDisplayContextMenu = true;

        this.contextMenuItems = this.getContextMenuItems(source);

        // Position de la souris utilisé pour placer le menu contextuel.
        this.rightClickMenuPositionX = event.clientX;
        this.rightClickMenuPositionY = event.clientY;

        // Récupère la sélection dans le champ Input. Le paramètre remplacera cette selection.
        this.currentSelectionStart = event.target['selectionStart'];
        this.currentSelectionEnd = event.target['selectionEnd'];

        this.contextMenu.openMenu(event);
    }

    /**
     * Levée lorsque l'utilisateur clique sur un élément du menu contextuel.
     * @param event : Événement clic du menu contextuel.
     */
    handleMenuItemClick(event: TemplateContextMenuItemEvent) {
        switch (event.source) {
            case 'content':
                let content = this.contentFormControl.value === null ? '' : this.contentFormControl.value;
                this.sms.content = content.substring(0, this.currentSelectionStart) + event.variable + content.substring(this.currentSelectionEnd);
                this.contentFormControl.setValue(this.sms.content);
                break;
            case 'to':
                this.addMobileNumberToRecipients(event.variable);
                break;
        }

        this.isDisplayContextMenu = false;
    }

    /**
     * Ajoute un n° de téléphone aux destinataires.
     * @param mobileNumber : N° de téléphone à ajouter aux destinataires.
     */
    addMobileNumberToRecipients(mobileNumber: string) {
        this.sms.to.push(mobileNumber);
        this.formGroup.get('template').setValue(this.sms);
    }

    /**
     * Retourne le style du menu contextuel.
     * @returns : Le style du menu contextuel.
     */
    getContextMenuStyle() {
        // Positionne le menu contextuel au niveau du pointeur de la souris.
        return {
            position: 'fixed',
            left: `${this.rightClickMenuPositionX}px`,
            top: `${this.rightClickMenuPositionY}px`,
        };
    }

    /**
     * Retourne les éléments du menu contextuel affichant les paramètres.
     * @param source : source du menu contextuel. Il s'agit du composant pour lequel le menu contextuel doit être affiché.
     * @returns La liste des éléments du menu contextuel.
     */
    getContextMenuItems(source: string) {
        let menuItems = [];

        if (source === 'to') {
            this.attributes
                .filter((x) => x.type === PropertyType.PhoneNumber)
                .sort((x, y) => x.header.localeCompare(y.header))
                .forEach((attribute) => {
                    if (!attribute.custom) {
                        menuItems.push(
                            new TemplateContextMenuItem(attribute.header, `{{data.FormatedFolder.${attribute.name[0].toUpperCase() + attribute.name.substring(1)}}}`, source)
                        );
                    } else {
                        menuItems.push(new TemplateContextMenuItem(attribute.header, `{{data.FormatedFolder.CustomAttributes["${attribute.name}"]}}`, source));
                    }
                });
        } else {
            // Elements du menu contextuel commun à toutes les organisations.
            menuItems.push(new TemplateContextMenuItem('Url de suivi du dossier', '{{data.TrackingUrl}}', source));

            // Elements du menu contextuel propre à l'organisation courante.
            this.attributes
                .sort((x, y) => x.header.localeCompare(y.header))
                .forEach((attribute) => {
                    if (!attribute.custom) {
                        menuItems.push(
                            new TemplateContextMenuItem(attribute.header, `{{data.FormatedFolder.${attribute.name[0].toUpperCase() + attribute.name.substring(1)}}}`, source)
                        );
                    } else {
                        menuItems.push(new TemplateContextMenuItem(attribute.header, `{{data.FormatedFolder.CustomAttributes["${attribute.name}"]}}`, source));
                    }
                });
        }

        return menuItems;
    }

    /**
     * Ferme le menu contextuel.
     */
    public CloseContextMenu() {
        this.isDisplayContextMenu = false;
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Annuler'.
     */
    public cancel(): void {
        this.canceled.emit(true);
    }

    /**
     * Appellée lorsque l'utilisateur colle du texte dans le champ 'Destinataires'.
     * @param event Evenement copier/coller contenant les n° de telephone à ajouter au champ 'Destinataires'.
     */
    public pasteMobileNumbers(event: ClipboardEvent): void {
        event.preventDefault();
        event.clipboardData
            .getData('Text')
            .split(/;|,|\n/)
            .forEach((value) => {
                if (value.trim() && new RegExp(this.smsPattern).test(value.trim()) === true) {
                    const smsNumber = new RegExp(this.smsPattern).exec(value.trim())[0];
                    this.sms.to.push(smsNumber);
                }
            });
    }

    /**
     * Affiche la barre de progression indiquant le nombre de caractères saisis par rapport au nombre total de caractères acceptés
     * @param inputText : Texte saisi
     */
    private displayProgressBar(inputText: string) {
        let currentLength = inputText.length;

        // Si la longueur du texte saisi est supérieure à la taille maximale acceptée, on tronque la saisie à la taille maximale.
        if (currentLength > this.smsMaxLength * this.smsMaxParts) {
            this.contentFormControl.setValue((this.contentFormControl.value as string).substring(0, this.smsMaxLength * this.smsMaxParts));
            currentLength = this.smsMaxLength * this.smsMaxParts;
        }

        // Barre de progression
        let progressWidth = currentLength / (this.smsMaxLength * this.smsMaxParts) <= 1 ? (currentLength / (this.smsMaxLength * this.smsMaxParts)) * 100 : 100;
        this.progressBar.nativeElement.style.width = `${progressWidth}%`;
        this.charsCounter.nativeElement.style.visibility = 'visible';

        // Affiche une barre de progression colorée en fonction du nombre de caractères saisis.
        if (progressWidth <= 60) {
            this.progressBar.nativeElement.style.backgroundColor = this.themesService.getCurrentTheme() === ThemesService.DARK_THEME ? '#3dbd3d' : '#5bc25b';
        } else if (progressWidth > 60 && progressWidth < 90) {
            this.progressBar.nativeElement.style.backgroundColor = '#ec9d08';
        } else {
            this.progressBar.nativeElement.style.backgroundColor = '#fb5757';
        }

        // Cacul du nombre de caractères du SMS courant et du nombre de SMS.
        let currentSmsLenght = currentLength % this.smsMaxLength;
        let smsPartsCount = Math.floor(currentLength / this.smsMaxLength + 1);

        if (currentSmsLenght === 0 && smsPartsCount === 11) {
            currentSmsLenght = this.smsMaxLength;
            smsPartsCount = this.smsMaxParts;
        }

        // Affichage du compteur de caractères.
        this.charsCounter.nativeElement.innerHTML = `caractères : ${currentSmsLenght} / ${this.smsMaxLength} - SMS : ${smsPartsCount} / ${this.smsMaxParts}`;
        this.charsCounter.nativeElement.style.visibility = 'visible';

        if (currentSmsLenght < 100) {
            this.charsCounter.nativeElement.style.color = this.themesService.getCurrentTheme() === ThemesService.DARK_THEME ? '#41f141' : '#5bc25b';
        } else if (currentSmsLenght < 140) {
            this.charsCounter.nativeElement.style.color = '#ec9d08';
        } else {
            this.charsCounter.nativeElement.style.color = '#fb5757';
        }
    }
}

export function MaxRecipientsValidator(sms: Sms): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!sms) {
            return null;
        }

        return sms.to.length > 5 ? { maxRecipients: true } : null;
    };
}
